// Pure data helpers of the suggestions panel (SPEC §23): API shapes, statuses and their badge tones,
// the filter chips with their counts, search, who may edit / delete / decide, the admin actions of a
// status and form validation. DOM-free.
import { matchesQuery, toMs } from '../automations/model.js';

/** Limits of server/modules/suggestions.js. */
export const TITLE_MAX = 120;
export const TEXT_MAX = 10000;
export const COMMENT_MAX = 2000;
export const NOTE_MAX = 2000;

/** The statuses, in their life order. */
export const STATUSES = ['proposed', 'approved', 'in_progress', 'done', 'rejected'];
/** The filter chips, in display order (Toutes · Proposées · Approuvées · En cours · Réalisées · Refusées). */
export const FILTERS = ['all', ...STATUSES];

/** Badge tone of each status (web/dom.js badge()). */
export const STATUS_TONES = Object.freeze({
  proposed: 'amber', approved: 'info', in_progress: 'running', done: 'success', rejected: 'muted',
});

/**
 * What an admin may do from each status (the server checks the same transitions, 409 otherwise):
 * approve / reject (review), start (→ in_progress), finish (→ done), reopen (in_progress → approved).
 */
const ADMIN_ACTIONS = Object.freeze({
  proposed: ['approve', 'reject'],
  approved: ['start', 'finish', 'reject'],
  in_progress: ['finish', 'reopen'],
  rejected: ['approve'],
  done: [],
});

/** The request each admin action sends: [path suffix, body]. */
const ACTION_REQUESTS = Object.freeze({
  approve: ['review', { decision: 'approved' }],
  reject: ['review', { decision: 'rejected' }],
  start: ['progress', { status: 'in_progress' }],
  finish: ['progress', { status: 'done' }],
  reopen: ['progress', { status: 'approved' }],
});

/** The tone of a status badge ('neutral' for an unknown one). */
export function statusTone(status) {
  return Object.prototype.hasOwnProperty.call(STATUS_TONES, status) ? STATUS_TONES[status] : 'neutral';
}

/** A filter from a route tab (#/suggestions/<filter>), or null when it is not one. */
export function resolveFilter(tab) {
  return FILTERS.includes(tab) ? tab : null;
}

/**
 * The filter shown when the panel opens without one: an admin starts on « Proposées » when some wait
 * for a decision, everyone else on « Toutes ».
 * @param {object[]} list
 * @param {boolean} isAdmin
 */
export function defaultFilter(list, isAdmin) {
  return isAdmin && (list || []).some((s) => s?.status === 'proposed') ? 'proposed' : 'all';
}

/** The suggestion of an API answer or an event: `{suggestion}` or the suggestion itself; null otherwise. */
export function suggestionOf(res) {
  const s = res?.suggestion && typeof res.suggestion === 'object' ? res.suggestion : res;
  return s && typeof s === 'object' && typeof s.id === 'string' && s.id ? s : null;
}

/** The list of a GET /api/suggestions answer (`{suggestions}` or an array). */
export function suggestionsOf(res) {
  const list = Array.isArray(res) ? res : Array.isArray(res?.suggestions) ? res.suggestions : [];
  return list.filter((s) => s && typeof s === 'object' && typeof s.id === 'string');
}

/** Newest first (createdAt), then by id; returns a new array. */
export function sortSuggestions(list) {
  return [...(list || [])].sort((a, b) => (toMs(b.createdAt) ?? 0) - (toMs(a.createdAt) ?? 0) || String(a.id).localeCompare(String(b.id)));
}

/**
 * Counts per filter chip.
 * @param {object[]} list
 * @returns {{all: number, proposed: number, approved: number, in_progress: number, done: number, rejected: number}}
 */
export function countByStatus(list) {
  const out = Object.fromEntries(FILTERS.map((f) => [f, 0]));
  for (const s of list || []) {
    out.all += 1;
    if (STATUSES.includes(s?.status)) out[s.status] += 1;
  }
  return out;
}

/**
 * The suggestions of a filter matching the search (every word in the title, text, author, admin
 * comment or work note), newest first.
 * @param {object[]} list
 * @param {{filter?: string, query?: string}} [opts]
 */
export function filterSuggestions(list, { filter = 'all', query = '' } = {}) {
  return sortSuggestions(list).filter((s) => (filter === 'all' || s.status === filter)
    && matchesQuery([s.title, s.text, s.authorName, s.review?.comment, s.work?.note, s.work?.version], query));
}

/** True when `me` wrote this suggestion. */
export function isMine(s, me) {
  return !!me?.id && s?.authorId === me.id;
}

/**
 * True for a suggestion toast (`notify`) about what `me` did themself (`actorId`, e.g. an admin's
 * own proposal reaching the admins): the shell shows no toast for it.
 * @param {object} data the notify event
 * @param {object|null} me
 */
export function isOwnNotify(data, me) {
  return data?.type === 'suggestion' && !!data.actorId && data.actorId === me?.id;
}

/** True when `me` is an administrator. */
export function isAdminUser(me) {
  return me?.role === 'admin';
}

/** The author edits their own suggestion while it is proposed; an admin edits any. */
export function canEdit(s, me) {
  return isAdminUser(me) || (isMine(s, me) && s?.status === 'proposed');
}

/** Same rule as editing: the author while proposed, an admin always. */
export function canDelete(s, me) {
  return canEdit(s, me);
}

/** The admin actions offered on a suggestion ([] for members and for a done one). */
export function adminActions(s, me) {
  if (!isAdminUser(me)) return [];
  return Object.prototype.hasOwnProperty.call(ADMIN_ACTIONS, s?.status) ? ADMIN_ACTIONS[s.status] : [];
}

/**
 * The request of an admin action: POST /api/suggestions/<id>/<review|progress> with its body; the
 * optional comment (review) or note (progress) is sent only when given.
 * @param {string} id
 * @param {'approve'|'reject'|'start'|'finish'|'reopen'} action
 * @param {string} [text]
 * @returns {{path: string, body: object}|null}
 */
export function actionRequest(id, action, text = '') {
  if (!Object.prototype.hasOwnProperty.call(ACTION_REQUESTS, action)) return null;
  const [kind, base] = ACTION_REQUESTS[action];
  const value = String(text ?? '').trim();
  const body = { ...base };
  if (value) body[kind === 'review' ? 'comment' : 'note'] = value;
  return { path: `/api/suggestions/${encodeURIComponent(id)}/${kind}`, body };
}

/** The short form of a commit hash (7 characters), '' when it is not one. */
export function shortCommit(commit) {
  const s = String(commit ?? '').trim();
  return /^[0-9a-f]{7,40}$/i.test(s) ? s.slice(0, 7) : '';
}

/** The form values as sent (title on one line, both trimmed). */
export function suggestionBody({ title = '', text = '' } = {}) {
  return { title: String(title).replace(/\s+/g, ' ').trim(), text: String(text).trim() };
}

/**
 * Checks the form; returns the string keys of the errors by field ({} when valid).
 * @param {{title?: string, text?: string}} form
 * @returns {{title?: string, text?: string}}
 */
export function validateSuggestion(form) {
  const { title, text } = suggestionBody(form);
  const errors = {};
  if (!title) errors.title = 'sug.error.titleRequired';
  else if (title.length > TITLE_MAX) errors.title = 'sug.error.titleTooLong';
  if (!text) errors.text = 'sug.error.textRequired';
  else if (text.length > TEXT_MAX) errors.text = 'sug.error.textTooLong';
  return errors;
}

/**
 * The fields changed by the edit form (PATCH body), null when nothing changed.
 * @param {object} s the suggestion
 * @param {{title: string, text: string}} form
 */
export function changedFields(s, form) {
  const next = suggestionBody(form);
  const patch = {};
  if (next.title !== String(s?.title ?? '')) patch.title = next.title;
  if (next.text !== String(s?.text ?? '')) patch.text = next.text;
  return Object.keys(patch).length ? patch : null;
}
